import type { CompetenceRecord } from "../../api/client";
import { DateField } from "../../components/DateField";
import { SelectField } from "../../components/SelectField";
import { TextField } from "../../components/TextField";

const SCHEMES = ["IRCA", "Exemplar Global", "RABQSA", "Other"] as const;
const GRADES = [
  "Certified Lead Auditor",
  "Lead Auditor",
  "Auditor",
  "Provisional Auditor",
] as const;

/**
 * Screen 13 — education and third-party auditor certification.
 *
 * There is no "add at least one" here, and that is deliberate. The data dictionary models
 * no certification table at all, so a minimum would be requiredness invented in the client
 * — and an auditor competent under their own body's scheme rather than a third party's is
 * not a defect in the record. What the server does refuse is *half* a certification.
 */
export function QualificationsStep({
  record,
  set,
  onAdd,
  onSet,
  onRemove,
  errorFor,
}: {
  record: CompetenceRecord;
  set: (patch: Partial<CompetenceRecord>) => void;
  onAdd: () => void;
  onSet: (index: number, patch: Record<string, string>) => void;
  onRemove: (index: number) => void;
  errorFor: (field: string) => string | undefined;
}) {
  const certifications = record.certifications ?? [];

  return (
    <div className="card">
      <h2>Education and auditor certification</h2>

      <TextField
        id="auditor-education"
        label="Highest relevant qualification"
        placeholder="e.g. BEng Manufacturing Systems, Politecnico di Milano"
        value={record.education ?? ""}
        onChange={(education) => set({ education })}
        error={errorFor("education")}
        required
      />

      <div className="hr" />
      <div className="between">
        <div className="eyebrow">Auditor certification</div>
        <button type="button" className="btn sec sm" onClick={onAdd}>
          Add certification
        </button>
      </div>
      <p className="sub" style={{ margin: "6px 0 12px" }}>
        Third-party auditor certification is how a certification body evidences competence to
        its accreditation body. Expiry drives an automatic status change here.
      </p>

      {certifications.map((entry, index) => (
        <div key={index} className="plan-ed" style={{ marginBottom: "10px" }}>
          <div className="grid2">
            <SelectField
              id={`certification-${index}-scheme`}
              label="Scheme"
              value={entry.scheme ?? ""}
              onChange={(scheme) => onSet(index, { scheme })}
              options={SCHEMES.map((value) => ({ value, label: value }))}
              error={errorFor(`certifications.${index}.scheme`)}
            />
            <SelectField
              id={`certification-${index}-grade`}
              label="Grade"
              value={entry.grade ?? ""}
              onChange={(grade) => onSet(index, { grade })}
              options={GRADES.map((value) => ({ value, label: value }))}
              error={errorFor(`certifications.${index}.grade`)}
            />
          </div>
          <div className="grid2">
            <TextField
              id={`certification-${index}-number`}
              label="Certificate number"
              value={entry.certificate_number ?? ""}
              onChange={(certificate_number) => onSet(index, { certificate_number })}
              error={errorFor(`certifications.${index}.certificate_number`)}
            />
            <DateField
              id={`certification-${index}-expiry`}
              label="Expiry"
              value={entry.expiry ?? ""}
              onChange={(expiry) => onSet(index, { expiry })}
              error={errorFor(`certifications.${index}.expiry`)}
            />
          </div>
          {errorFor(`certifications.${index}`) ? (
            <p className="err" role="alert">
              {errorFor(`certifications.${index}`)}
            </p>
          ) : null}
          <div style={{ marginTop: "9px" }}>
            <button
              type="button"
              className="btn danger sm"
              onClick={() => onRemove(index)}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
